import { faInfoCircle, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Label,
  RadioButton,
  Text,
  Textarea,
  Tooltip,
  ValidationMessage,
} from '@modusign/design-system';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form-v7';
import { useMount } from 'react-use';

import EmailAutoCompleteInput, { emailValidator } from '@app/components/EmailAutoCompleteInput';
import { CONTACT_INFO_MAX_LENGTH } from '@app/constants/contactInfoMaxLength';
import useContacts from '@app/features/addressBook/hooks/useContacts';
import { validateName, validatePhoneNumber } from '@app/features/addressBook/libs';
import { CreateContactFormValue } from '@app/features/addressBook/types';
import Contact from '@app/features/addressBook/types/Contact';
import {
  getBizNumberFormat,
  getFormattedShortPhoneNumber,
  removeNotNumberFromString,
} from '@app/libs/inputFormat';

import ContactCustomInput from './ContactCustomInput';
import DefaultBadge from './DefaultBadge';
import InputWithLabelWrapper from './InputWithLabelWrapper';

export type FormData = Omit<Contact, 'createdAt' | 'id' | 'updatedAt'>;

export interface ContactInputFormProps {
  cancelButtonText?: string;
  contactId?: string;
  defaultValues?: Partial<FormData>;
  submitButtonText?: string;
  onCancel: () => void;
  onSubmit: (contactFormValue: CreateContactFormValue) => void;
}

function ContactInputForm({
  cancelButtonText = '취소',
  contactId,
  defaultValues,
  submitButtonText = '저장',
  onCancel,
  onSubmit,
}: ContactInputFormProps) {
  const [isAdditionalInfoOpen, setIsAdditionalInfoOpen] = useState(false);

  useMount(async () => {
    // 부가정보 값이 있으면 추가입력란 체크, 화면에 입력란을 표시
    if (
      defaultValues?.companyName ||
      defaultValues?.departmentPosition ||
      defaultValues?.bizNumber
    ) {
      setIsAdditionalInfoOpen(true);
    }
  });

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
  });

  const nameValue = watch('name');
  const defaultForwardingMethodValue = watch('defaultForwardingMethod', null);
  const description = watch('description', '');

  const submitDisabled = !isDirty || !isValid;

  const {
    data: contactsData,
    refetch,
    remove,
  } = useContacts(
    { exactly: true, limit: 10, name: nameValue, offset: 0 },
    {
      enabled: false,
      queryKey: ['contactInputFormFetchContacts', nameValue],
    }
  );

  const hasDuplicatedContact = useMemo(() => {
    const contacts = contactsData?.contacts ?? [];
    if (contactId) {
      return contacts.filter((contact) => contact.id !== contactId).length > 0;
    }
    return contacts.length > 0;
  }, [contactId, contactsData?.contacts]);

  useEffect(() => {
    if (defaultForwardingMethodValue) {
      trigger('email');
      trigger('phoneNumber');
    }
  }, [defaultForwardingMethodValue, trigger]);

  useEffect(() => {
    if (hasDuplicatedContact) {
      setIsAdditionalInfoOpen(true);
    }
  }, [hasDuplicatedContact]);

  const defaultForwardingMethodFieldProps = register('defaultForwardingMethod');

  return (
    <>
      <InputWithLabelWrapper
        label={
          <Label
            htmlFor="contact-input-form-name"
            sx={{
              color: 'textNeutralBase',
              fontWeight: 'medium',
            }}
          >
            이름
            <Text color="textNegativeBase" fontWeight="bold">
              *
            </Text>
          </Label>
        }
      >
        <ContactCustomInput
          aria-label="이름"
          id="contact-input-form-name"
          isInvalid={!!errors.name}
          maxLength={30}
          placeholder="30자 이내로 입력"
          {...register('name', {
            validate: (value) => validateName(value),
          })}
          type="text"
          onBlur={(event) => {
            trigger('name');
            const { value } = event.target;
            const isValid = validateName(value) === true;
            if (isValid) {
              refetch();
            }
          }}
          onClear={nameValue ? () => setValue('name', '') : undefined}
          onFocus={() => {
            remove();
          }}
        />
        {errors.name && <ValidationMessage>{errors.name.message}</ValidationMessage>}
        {!errors.name && hasDuplicatedContact && (
          <Text fontSize="sm">
            {/* FIXME(@GilHyeon): DS 시멘틱 컬러 textPrimaryDark 추가 후 컬러 수정하기 */}
            <Box as="span" color="orangeYellow900" mr={2}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Box>
            같은 이름의 연락처가 있습니다. 연락처 구분을 위해 부가 정보를 입력해 주세요.
          </Text>
        )}
      </InputWithLabelWrapper>
      <Divider mb={7} mt={2} />
      <Box mb={4} px={4}>
        <Text fontWeight="medium">전자계약 수신처</Text>
        <Text color="textNegativeBase" fontWeight="bold">
          *
        </Text>
        <Text color="textNeutralLight" fontSize="xs" ml={2}>
          최소 한 개 입력
        </Text>
      </Box>
      <InputWithLabelWrapper
        label={
          <Label
            sx={{
              color: 'textNeutralLight',
            }}
          >
            <Flex>
              <Box>기본 전송 수단</Box>
              <Tooltip content="서명 요청 알림과 완료 문서가 전송되는 연락처" placement="top-start">
                <Box as="span" ml={1}>
                  <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                </Box>
              </Tooltip>
            </Flex>
          </Label>
        }
      >
        <Flex alignItems="center" gap={4} height="36px">
          <Flex alignItems="center">
            <RadioButton
              id="contact-input-form-default-signing-method-kakao"
              {...defaultForwardingMethodFieldProps}
              value="KAKAO"
              onClick={(event) => {
                defaultForwardingMethodFieldProps.onChange(event);
                trigger(['email', 'phoneNumber']);
              }}
            />
            <Label
              htmlFor="contact-input-form-default-signing-method-kakao"
              mb={0}
              ml={2}
              sx={{
                cursor: 'pointer',
                fontWeight: 'medium',
              }}
            >
              휴대폰
            </Label>
          </Flex>
          <Flex alignItems="center">
            <RadioButton
              id="contact-input-form-default-signing-method-email"
              {...defaultForwardingMethodFieldProps}
              value="EMAIL"
              onClick={(event) => {
                defaultForwardingMethodFieldProps.onChange(event);
                trigger(['email', 'phoneNumber']);
              }}
            />
            <Label
              htmlFor="contact-input-form-default-signing-method-email"
              mb={0}
              ml={2}
              sx={{
                cursor: 'pointer',
                fontWeight: 'medium',
              }}
            >
              이메일
            </Label>
          </Flex>
        </Flex>
      </InputWithLabelWrapper>
      <InputWithLabelWrapper
        label={
          <Flex>
            <Label
              htmlFor="contact-input-form-phone-number"
              mr={2}
              sx={{
                color: 'textNeutralLight',
              }}
            >
              휴대폰
            </Label>
            {defaultForwardingMethodValue === 'KAKAO' && <DefaultBadge />}
          </Flex>
        }
      >
        <Controller
          control={control}
          defaultValue={defaultValues?.phoneNumber ?? ''}
          name="phoneNumber"
          render={({ field: { name, ref, value, onChange }, fieldState: { invalid } }) => (
            <ContactCustomInput
              aria-label="휴대폰"
              id="contact-input-form-phone-number"
              isInvalid={invalid}
              maxLength={9}
              name={name}
              placeholder="8자리 숫자만 입력"
              prefixNode={{
                element: (
                  <Text lineHeight="1.3em" sx={{ position: 'absolute', top: '9px', left: 4 }}>
                    010-
                  </Text>
                ),
                width: '44px',
              }}
              ref={ref}
              type="tel"
              value={getFormattedShortPhoneNumber(value ?? '')}
              onChange={(event) => {
                if (event.target.value) {
                  trigger('email');
                }

                if (!defaultForwardingMethodValue) {
                  setValue('defaultForwardingMethod', 'KAKAO');
                }
                onChange(event);
              }}
              onClear={value ? () => onChange('') : undefined}
            />
          )}
          rules={{
            validate: (value) => {
              if (defaultForwardingMethodValue === 'EMAIL' && !value) {
                return true;
              }
              return validatePhoneNumber(value ?? '');
            },
          }}
        />
        {errors.phoneNumber && <ValidationMessage>{errors.phoneNumber.message}</ValidationMessage>}
      </InputWithLabelWrapper>
      <InputWithLabelWrapper
        label={
          <Flex>
            <Label
              htmlFor="contact-input-form-email"
              sx={{
                color: 'textNeutralLight',
              }}
            >
              이메일
            </Label>
            {defaultForwardingMethodValue === 'EMAIL' && <DefaultBadge />}
          </Flex>
        }
      >
        <Controller
          control={control}
          defaultValue={defaultValues?.email ?? ''}
          name="email"
          render={({ field: { name, value, onChange } }) => (
            <EmailAutoCompleteInput
              aria-label="이메일"
              autoFocus={false}
              domainSuggestions={[
                { id: 'naver', label: 'naver.com' },
                { id: 'gmail', label: 'gmail.com' },
                { id: 'hanmail', label: 'hanmail.net' },
                { id: 'daum', label: 'daum.net' },
                { id: 'kakao', label: 'kakao.com' },
                { id: 'nate', label: 'nate.com' },
              ]}
              error={errors.email?.message}
              id="contact-input-form-email"
              initialValue={value ?? ''}
              maxLength={CONTACT_INFO_MAX_LENGTH}
              name={name}
              placeholder="이메일 주소"
              onChange={(v) => {
                if (v) {
                  trigger('phoneNumber');
                }
                if (!defaultForwardingMethodValue) {
                  setValue('defaultForwardingMethod', 'EMAIL');
                }
                onChange(v);
              }}
              onClear={value ? () => onChange('') : undefined}
            />
          )}
          rules={{
            validate: (value) => {
              if (defaultForwardingMethodValue === 'KAKAO' && !value) {
                return true;
              }
              return emailValidator(value ?? '');
            },
          }}
        />
      </InputWithLabelWrapper>
      <Divider mb={7} mt={2} />
      <InputWithLabelWrapper
        label={
          <Label
            htmlFor="contact-input-form-description"
            sx={{
              color: 'textNeutralBase',
              fontWeight: 'medium',
            }}
          >
            설명
          </Label>
        }
      >
        <Textarea
          id="contact-input-form-description"
          maxLength={100}
          placeholder="100자 이하로 입력"
          {...register('description', { required: false })}
        />
        <Flex justifyContent="flex-end">
          <Text as="p" color="textNeutralLight">
            {description?.length || 0}/100
          </Text>
        </Flex>
      </InputWithLabelWrapper>
      <Divider mb={7} mt={2} />
      <InputWithLabelWrapper
        label={
          <Flex alignItems="center">
            <Label
              htmlFor="contact-input-form-email"
              sx={{
                color: 'textNeutralBase',
                fontWeight: 'medium',
              }}
            >
              부가정보
            </Label>
            <Text color="textNeutralLight" fontSize="xs" mb={2} ml={3}>
              회사명, 직위, 사업자등록번호 등
            </Text>
          </Flex>
        }
        labelWidth="350px"
      >
        <Flex alignItems="center" height="36px" justifyContent="flex-end">
          <Flex alignItems="center">
            <Checkbox
              checked={isAdditionalInfoOpen}
              id="contact-input-form-form-isAdditionalInfoOpen"
              name="isAdditionalInfoOpen"
              weight="lg"
              onChange={(event) => {
                const { checked } = event.target;
                setIsAdditionalInfoOpen(checked);
                if (!checked) {
                  setValue('companyName', '');
                  setValue('departmentPosition', '');
                  setValue('bizNumber', '');
                  setTimeout(() => {
                    trigger();
                  }, 10);
                }
              }}
            />
            <Label
              htmlFor="contact-input-form-form-isAdditionalInfoOpen"
              mb={0}
              ml={3}
              sx={{
                cursor: 'pointer',
                fontSize: 'lg',
                fontWeight: 'medium',
              }}
            >
              추가 입력
            </Label>
          </Flex>
        </Flex>
      </InputWithLabelWrapper>
      <Box display={isAdditionalInfoOpen ? 'block' : 'none'}>
        <InputWithLabelWrapper
          label={
            <Label
              htmlFor="contact-input-form-companyName"
              sx={{
                color: 'textNeutralLight',
              }}
            >
              회사 또는 단체명
            </Label>
          }
        >
          <Controller
            control={control}
            defaultValue={defaultValues?.companyName ?? ''}
            name="companyName"
            render={({ field: { name, value, onChange } }) => (
              <ContactCustomInput
                id="contact-input-form-companyName"
                isInvalid={!!errors.companyName}
                maxLength={30}
                name={name}
                placeholder="30자 이하로 입력"
                value={value ?? undefined}
                onChange={onChange}
                onClear={value ? () => onChange('') : undefined}
              />
            )}
            rules={{
              validate: (value) => {
                if (!value) {
                  return true;
                }
                return validateName(value);
              },
            }}
          />
          {errors.companyName && (
            <ValidationMessage>{errors.companyName.message}</ValidationMessage>
          )}
        </InputWithLabelWrapper>

        <InputWithLabelWrapper
          label={
            <Label
              htmlFor="contact-input-form-departmentPosition"
              sx={{
                color: 'textNeutralLight',
              }}
            >
              직급 또는 직책
            </Label>
          }
        >
          <Controller
            control={control}
            defaultValue={defaultValues?.departmentPosition ?? ''}
            name="departmentPosition"
            render={({ field: { name, value, onChange } }) => (
              <ContactCustomInput
                id="contact-input-form-departmentPosition"
                isInvalid={!!errors.departmentPosition}
                maxLength={30}
                name={name}
                placeholder="30자 이하로 입력"
                value={value ?? undefined}
                onChange={onChange}
                onClear={value ? () => onChange('') : undefined}
              />
            )}
            rules={{
              validate: (value) => {
                if (!value) {
                  return true;
                }
                return validateName(value);
              },
            }}
          />
          {errors.departmentPosition && (
            <ValidationMessage>{errors.departmentPosition.message}</ValidationMessage>
          )}
        </InputWithLabelWrapper>

        <InputWithLabelWrapper
          label={
            <Label
              htmlFor="contact-input-form-bizNumber"
              sx={{
                color: 'textNeutralLight',
              }}
            >
              사업자등록번호
            </Label>
          }
        >
          <Controller
            control={control}
            defaultValue={defaultValues?.bizNumber ?? ''}
            name="bizNumber"
            render={({ field: { name, value, onChange } }) => (
              <ContactCustomInput
                id="contact-input-form-bizNumber"
                isInvalid={!!errors.bizNumber}
                maxLength={12}
                name={name}
                placeholder="10자 이하로 입력"
                value={getBizNumberFormat(value ?? '')}
                onChange={onChange}
                onClear={value ? () => onChange('') : undefined}
              />
            )}
            rules={{
              validate: (value) => {
                const numberOnlyValue = removeNotNumberFromString(value ?? '');
                if (numberOnlyValue && numberOnlyValue.length !== 10) {
                  return '10자리 숫자만 입력해 주세요.';
                }
                return true;
              },
            }}
          />
          {errors.bizNumber && <ValidationMessage>{errors.bizNumber.message}</ValidationMessage>}
        </InputWithLabelWrapper>
      </Box>
      <Box mt={10} textAlign="right">
        <Button variant="subtle" weight="light" onClick={onCancel}>
          {cancelButtonText}
        </Button>
        <Button
          disabled={submitDisabled}
          ml={2}
          variant="primary"
          weight="light"
          onClick={handleSubmit(
            ({
              bizNumber,
              companyName,
              defaultForwardingMethod,
              departmentPosition,
              description,
              email,
              name,
              phoneNumber,
            }) => {
              if (defaultForwardingMethod) {
                const removeDashPhoneNumber = removeNotNumberFromString(phoneNumber || '');
                const transformedPhoneNumber = removeDashPhoneNumber
                  ? `010${removeDashPhoneNumber}`
                  : null;

                onSubmit({
                  bizNumber: bizNumber || null,
                  companyName: companyName || null,
                  defaultForwardingMethod,
                  departmentPosition: departmentPosition || null,
                  description: description || null,
                  email: email || null,
                  name,
                  phoneNumber: transformedPhoneNumber,
                });
              }
            }
          )}
        >
          {submitButtonText}
        </Button>
      </Box>
    </>
  );
}

export default ContactInputForm;
